import { useEffect } from 'react';
import { track } from '@/lib/analytics';

/**
 * Scroll depth and active reading time for one article — phases.md Phase 16.
 *
 * The two measurements share a hook because they share a lifetime: both start when the
 * story renders, both report when the reader leaves it, and splitting them would mean
 * two sets of listeners racing each other to the same unmount.
 *
 * **Active** reading time, not time-on-page. The timer pauses when the tab is hidden,
 * which is the difference between "read for four minutes" and "left the tab open
 * overnight" — and the reason the average in the content report is a number an editor
 * can act on rather than a measure of how many people use a lot of tabs.
 *
 * Scroll depth is reported at 25/50/75/100 and each milestone fires once. Milestones
 * rather than a final percentage because the shape of the drop-off is the interesting
 * part: half the readers reaching 50% and none reaching 75% says something specific
 * about a story that "average depth 48%" does not.
 */
export function useArticleEngagement(articleId: number | undefined, path: string): void {
  useEffect(() => {
    if (articleId === undefined || typeof window === 'undefined') return undefined;

    const milestones = [25, 50, 75, 100];
    const reached = new Set<number>();
    let activeMs = 0;
    let since = document.visibilityState === 'visible' ? Date.now() : 0;

    const accumulate = () => {
      if (since !== 0) {
        activeMs += Date.now() - since;
        since = 0;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        since = Date.now();
      } else {
        accumulate();
      }
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      // A story shorter than the viewport is fully read the moment it renders; treating
      // it as 0% would make short pieces look unread forever.
      const percent =
        scrollable <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / scrollable) * 100));

      for (const milestone of milestones) {
        if (percent >= milestone && !reached.has(milestone)) {
          reached.add(milestone);
          track({ type: 'scroll_depth', path, articleId, value: milestone });
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('scroll', onScroll, { passive: true });
    // Once on mount, so a story that fits on one screen records its 100%.
    onScroll();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('scroll', onScroll);
      accumulate();

      const seconds = Math.round(activeMs / 1000);
      // Under five seconds is a mis-click, not a read. The server caps the upper end.
      if (seconds >= 5) {
        track({ type: 'read_time', path, articleId, value: Math.min(seconds, 86_400) });
      }
    };
  }, [articleId, path]);
}
